import { ReactNode, useEffect, useState } from 'react';
import { LoginQueryParams } from '@constants/paths';
import BankIdModal from '@organisms/BankIdModal/BankIdModal';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { StyledCheckoutSubmitButton } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { BankIdSignContextProvider } from '@organisms/BankIdModal/BankIdSignContextProvider';
import usePlaceOrder from '@hooks/usePlaceOrder';
import { useSearchParams } from 'next/navigation';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
  setError: (error: ErrorDataType) => void;
}

const InvoiceButton = ({ children, disabled = false, possiblyWrongStoreConfirmed, setError }: Props) => {
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [showBankIdModal, setShowBankIdModal] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const openBankIdModal = () => {
    setShowBankIdModal(true);
  };

  const { placeOrderHandler } = usePlaceOrder({
    possiblyWrongStoreConfirmed,
    setError,
    setPaymentInProgress,
    openBankIdModal,
  });

  useEffect(() => {
    if (searchParams?.get('from') === LoginQueryParams.LOGIN_CONFIRM_SUCCESS) {
      placeOrderHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      {showBankIdModal && (
        <BankIdSignContextProvider>
          <BankIdModal
            onClose={() => {
              setShowBankIdModal(false);
              setPaymentInProgress(false);
            }}
            onSuccess={async (orderRef) => {
              setShowBankIdModal(false);
              await placeOrderHandler(orderRef);
            }}
          />
        </BankIdSignContextProvider>
      )}
      <StyledCheckoutSubmitButton
        variant="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await placeOrderHandler();
          }
        }}
        type="submit"
        data-testid="invoice-checkout-button"
        disabled={disabled || paymentInProgress}
        isSubmitting={paymentInProgress}
      >
        {children}
      </StyledCheckoutSubmitButton>
    </>
  );
};

export default InvoiceButton;
